// Money and text formatting shared by the engine's parts. Pure functions,
// covered by format.test.ts.

export function money(n: number): string {
  return '£' + Math.round(n).toLocaleString('en-GB');
}

export function parseMoneyInput(raw: string): number {
  return Number(String(raw).replace(/[^\d]/g, '')) || 0;
}

export function formatMoneyInput(n: number): string {
  return n.toLocaleString('en-GB');
}

export function formatPct(n: number): string {
  return String(Math.round(n * 100) / 100);
}

export function escapeHtml(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

export function labelFor(options: [string, string][], value: string): string {
  const found = options.find((o) => o[0] === value);
  return found ? found[1] : '';
}

/**
 * The region a UK postcode is in, from its area letters (the part before the
 * first digit): areaRegions maps region → areas; anything else is England &
 * Wales. A heuristic, close enough for pricing.
 */
/** The postcode area: the letters before the first digit ("BS1 4DJ" → "BS"). */
export function postcodeArea(postcode: string): string {
  const match = String(postcode || '').trim().toUpperCase().match(/^[A-Z]{1,2}/);
  return match ? match[0] : '';
}

export function classifyUkPostcode(areaRegions: Record<string, string[]>, postcode: string): string {
  const area = postcodeArea(postcode);
  for (const [region, areas] of Object.entries(areaRegions)) {
    if (areas.includes(area)) return region;
  }
  return 'england_wales';
}

/**
 * Reformats a money input with thousands commas as the customer types,
 * keeping the cursor after the same number of digits. Returns the number.
 */
export function applyMoneyInputFormatting(input: HTMLInputElement): number {
  const digitsBeforeCursor = input.value.slice(0, input.selectionStart ?? input.value.length).replace(/[^\d]/g, '').length;
  const numericValue = parseMoneyInput(input.value);
  const formatted = formatMoneyInput(numericValue);
  input.value = formatted;
  let digitsSeen = 0;
  let newCursor = formatted.length;
  if (digitsBeforeCursor === 0) {
    newCursor = 0;
  } else {
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) digitsSeen++;
      if (digitsSeen === digitsBeforeCursor) { newCursor = i + 1; break; }
    }
  }
  try { input.setSelectionRange(newCursor, newCursor); } catch { /* not focusable */ }
  return numericValue;
}
